import { fetcher, useClientPortalAPI, useQueryParams } from "@certn/api";
import { Stack, Text } from "@certn/cascadia";
import { Container, ExpandableCard, Icon, Loader } from "@certn/cascadia-react";
import { Skeleton } from "@certn/cascadia-react/patterns/skeleton/Skeleton";
import { css } from "@emotion/css";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { PaymentProfileList } from "./PaymentProfileList";
import { useClientAccount } from "../../../hooks/useClientAccount";
import { OrderContext } from "../../../pages/order/OrderContext";
import { useOrderBillingProfile } from "../../../pages/order/steps/useOrderBillingProfile";
import {
  doesCardHaveIcon,
  getPaymentIcon,
} from "../../../pages/payment-profiles/helpers";
import { StripeSetupIntent } from "../../stripe/StripeSetupIntent";

export function PaymentProfile() {
  const { order } = useContext(OrderContext);
  const { params, clearParam } = useQueryParams();
  const { t } = useTranslation();
  const { client } = useClientAccount();
  const { data: profile, isLoading, mutate } = useOrderBillingProfile();
  // Paginated total for the first-card embed baseline (DAT-1731 Bugbot).
  const {
    data: paymentProfilesPage,
    error: paymentProfilesError,
    isLoading: paymentProfilesLoading,
    mutate: mutatePaymentProfiles,
  } = useClientPortalAPI(
    client?.id
      ? `/api/internal/client-portal/customer-accounts/${client.id}/payment-profiles/`
      : null
  );
  const [isExpanded, setIsExpanded] = useState(!profile);
  const [waitForStripe, setWaitForStripe] = useState(false);
  // Bumped on unmount so in-flight awaits do not call mutate / onSave on an
  // unmounted tree. set-default still runs (DAT-1731 Bugbot).
  const firstCardWorkGenerationRef = useRef(0);
  const redirectHandledRef = useRef(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const redirectWorkGenerationRef = useRef(0);
  const onToggle = useCallback(() => setIsExpanded((prev) => !prev), []);
  const onSave = useCallback(() => {
    setIsExpanded((prev) => !prev);
    mutate();
  }, [mutate]);

  // Poll already confirmed a new profile exists — set-default immediately.
  // Do not clear this work on unmount; only skip UI updates after leave.
  const handleFirstCardClose = useCallback(async () => {
    const generation = ++firstCardWorkGenerationRef.current;
    try {
      await fetcher.submit(
        `/api/internal/client-portal/order/${order.group}/set-default-payment-profile/`
      );
    } catch (error) {
      console.error(error);
    }
    if (generation !== firstCardWorkGenerationRef.current) return;
    try {
      await Promise.all([mutate(), mutatePaymentProfiles()]);
    } catch (error) {
      console.error(error);
    }
    if (generation !== firstCardWorkGenerationRef.current) return;
    onSave();
  }, [mutate, mutatePaymentProfiles, onSave, order.group]);

  const handleFirstCardCancel = useCallback(() => {
    setIsExpanded(false);
  }, []);

  useEffect(() => {
    return () => {
      firstCardWorkGenerationRef.current += 1;
      redirectWorkGenerationRef.current += 1;
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  // Full-page 3DS return: schedule set-default once. clearParam must not cancel
  // the timer (DAT-1731 Bugbot — redirect_status clear re-ran the effect).
  useEffect(() => {
    const redirectStatus = params["redirect_status"];
    if (!redirectStatus || redirectHandledRef.current) return;
    redirectHandledRef.current = true;

    if (params["open-new-payment-profile-modal"]) {
      setIsExpanded(true);
      clearParam("open-new-payment-profile-modal");
    }
    setWaitForStripe(true);
    clearParam("redirect_status");

    const generation = ++redirectWorkGenerationRef.current;
    redirectTimerRef.current = setTimeout(async () => {
      try {
        await fetcher.submit(
          `/api/internal/client-portal/order/${order.group}/set-default-payment-profile/`
        );
      } catch (error) {
        console.error(error);
      }
      if (generation !== redirectWorkGenerationRef.current) return;
      try {
        await mutatePaymentProfiles();
      } catch (error) {
        console.error(error);
      }
      if (generation !== redirectWorkGenerationRef.current) return;
      setWaitForStripe(false);
      onSave();
    }, 1000);
  }, [clearParam, mutatePaymentProfiles, onSave, order.group, params]);

  useEffect(() => {
    if (!profile?.payment_profile) {
      setIsExpanded(true);
    }
  }, [profile?.payment_profile]);

  if (!profile || waitForStripe) {
    if (isLoading || waitForStripe) {
      return (
        <ExpandableCard
          title={
            <Text style="global/heading-medium">{t("Payment method")}</Text>
          }
          subtitle={<Skeleton width={184} />}
        />
      );
    }

    return null;
  }

  const showFirstCardEmbed =
    !profile.payment_profile &&
    profile.customer_account?.paymentprofile_set.length === 0;
  // Prefer API count; if the request failed, fall back to 0 so checkout is not
  // blocked when the embedded set is already empty (DAT-1731 Bugbot).
  const firstCardBaseline =
    typeof paymentProfilesPage?.count === "number"
      ? paymentProfilesPage.count
      : paymentProfilesError
        ? 0
        : undefined;

  return (
    <ExpandableCard
      title={<Text style="global/heading-medium">{t("Payment method")}</Text>}
      expanded={isExpanded}
      iconLabel={profile && !isExpanded ? t("Change") : undefined}
      onToggle={onToggle}
      subtitle={
        profile.payment_profile && (
          <Stack orientation="horizontal" gap="space-2">
            {profile.payment_profile.brand &&
            doesCardHaveIcon(profile.payment_profile.brand) ? (
              <img
                src={
                  profile.payment_profile.brand
                    ? getPaymentIcon(profile.payment_profile.brand)
                    : ""
                }
                alt={profile.payment_profile.brand || undefined}
                width="24px"
                height="24px"
              />
            ) : (
              <Icon name="credit_card" size="space-6" />
            )}

            <Text style="global/body-secondary">
              {t("Card ending in •••• {{number}}", {
                number: profile.payment_profile.number,
              })}
            </Text>
          </Stack>
        )
      }
    >
      {showFirstCardEmbed && (
        <Container className={styles.container}>
          {typeof firstCardBaseline === "number" ? (
            <StripeSetupIntent
              defaultAddress={{
                country: profile.address.country || undefined,
                postal_code: profile.address.postal_code || undefined,
              }}
              handleClose={handleFirstCardClose}
              onCancel={handleFirstCardCancel}
              initialPaymentProfileCount={firstCardBaseline}
            />
          ) : paymentProfilesLoading || client?.id ? (
            <div className={styles.loader}>
              <Loader />
            </div>
          ) : null}
        </Container>
      )}
      {profile.customer_account?.paymentprofile_set?.length &&
        profile.customer_account?.paymentprofile_set?.length > 0 && (
          <PaymentProfileList onClose={onToggle} />
        )}
    </ExpandableCard>
  );
}

const styles = {
  container: css({
    width: "unset",
    paddingLeft: "var(--space-4)",
    paddingRight: "var(--space-4)",
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-2)",
  }),
  loader: css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  }),
};
